import { ChangeRequest } from '@app/core/model/change-request.model'
import { ScopeConfiguration, ScopeVersion } from '@app/core/model/scope-version'
import { createSelector } from '@ngrx/store'
import { ChangeRequestLoadingState, ChangeRequestState } from '../../model/change-request.state.model'
import { getScopeOverviewState, State } from '@app/features/scope-overview/store/reducers'
import { ChangeItemAdapter } from '../adapters/change-item.adapter'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Dictionary, groupBy, keyBy } from 'lodash'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { mapChangeRequestSections } from '../../util'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'

const getFeatureState = createSelector(getScopeOverviewState, (state: State): ChangeRequestState => state.changeRequest)

// Loading State Selectors
const selectChangeRequestLoadingState = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequestLoadingState => state?.loading
)

const selectChangeRequestLoading = createSelector(
  selectChangeRequestLoadingState,
  (loadingState: ChangeRequestLoadingState): boolean => loadingState?.changeRequest
)

const selectPreviewScopeLoading = createSelector(
  selectChangeRequestLoadingState,
  (loadingState: ChangeRequestLoadingState): boolean => loadingState?.previewScope
)

const selectAnyLoading = createSelector(
  selectChangeRequestLoadingState,
  (loadingState: ChangeRequestLoadingState): boolean =>
    loadingState ? Object.values(loadingState).some(Boolean) : false
)

// Change Items Entity Selectors
const changeItemsAdapterSelectors = ChangeItemAdapter.getSelectors()

const selectChangeItemsState = createSelector(getFeatureState, (state: ChangeRequestState) => state?.changeItems)

const selectAllChangeItems = createSelector(selectChangeItemsState, changeItemsAdapterSelectors.selectAll)

const selectChangeItemEntities = createSelector(selectChangeItemsState, changeItemsAdapterSelectors.selectEntities)

// Change Request State Selectors
const selectCurrentChangeRequest = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequest | undefined => state?.currentChangeRequest
)

const changeRequestAbleToMerge = createSelector(
  getFeatureState,
  (state: ChangeRequestState): boolean | undefined => state?.ableToMerge
)

const selectChangeRequests = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ChangeRequest[] => state?.changeRequests
)
const selectChangeRequestsMap = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Dictionary<ChangeRequest> => keyBy(state?.changeRequests, 'id')
)

const selectPreviewScope = createSelector(
  getFeatureState,
  (state: ChangeRequestState): ScopeVersion | undefined => state?.previewScope
)

const selectPreviewConfiguration = createSelector(
  selectPreviewScope,
  (previewScope: ScopeVersion): ScopeConfiguration => previewScope?.configuration || {}
)

const selectPreviewDeliverables = createSelector(
  selectPreviewScope,
  (previewScope: ScopeVersion): Deliverable[] => previewScope?.deliverables || []
)

const selectPreviewDeliverablesMap = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Dictionary<Deliverable> => state?.previewDeliverablesMap
)

// Mapped Deliverables Selectors
const selectMappedDeliverables = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Deliverable[] => state?.mappedDeliverables
)
const selectMappedUnsectionedDeliverables = createSelector(
  selectMappedDeliverables,
  (deliverables: Deliverable[]): Deliverable[] => filterUnsectionedDeliverables(deliverables)
)

const selectMappedTrackedDeliverables = createSelector(
  getFeatureState,
  (state: ChangeRequestState): Deliverable[] => state?.mappedTrackedDeliverables
)
const selectMappedUnsectionedTrackedDeliverables = createSelector(
  selectMappedTrackedDeliverables,
  (deliverables: Deliverable[]): Deliverable[] => filterUnsectionedDeliverables(deliverables)
)

const selectMappedDeliverablesBySection = createSelector(
  selectMappedDeliverables,
  (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBySection(deliverables)
)
const selectMappedTrackedDeliverablesBySection = createSelector(
  selectMappedTrackedDeliverables,
  (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBySection(deliverables)
)

const selectMappedDeliverableSections = (isTracked: boolean) =>
  createSelector(
    ScopeOverviewSelectors.selectCurrentScope,
    selectPreviewScope,
    isTracked ? selectMappedTrackedDeliverablesBySection : selectMappedDeliverablesBySection,
    (
      scope: ScopeVersion,
      previewScope: ScopeVersion,
      deliverablesBySection: Dictionary<Deliverable[]>
    ): ScopeSection[] => mapChangeRequestSections(scope, previewScope, deliverablesBySection)
  )

// Net Changes Selectors
const selectNetChangesDeliverables = createSelector(
  selectMappedTrackedDeliverables,
  (deliverables: Deliverable[]): Deliverable[] =>
    (deliverables || []).filter((d) => d.hasChanges || d.isRemoved || d.isAdded)
)

const selectNetChangesUnsectionedDeliverables = createSelector(
  selectNetChangesDeliverables,
  (deliverables: Deliverable[]): Deliverable[] => filterUnsectionedDeliverables(deliverables)
)

const selectNetChangesDeliverablesBySection = createSelector(
  selectNetChangesDeliverables,
  (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBySection(deliverables)
)

const selectNetChangesSections = createSelector(
  ScopeOverviewSelectors.selectCurrentScope,
  selectPreviewScope,
  selectNetChangesDeliverablesBySection,
  (scope: ScopeVersion, previewScope: ScopeVersion, deliverablesBySection: Dictionary<Deliverable[]>): ScopeSection[] =>
    mapChangeRequestSections(scope, previewScope, deliverablesBySection)
)

export const ChangeRequestSelectors = {
  selectChangeRequestLoading,
  selectPreviewScopeLoading,
  selectAnyLoading,

  changeRequestAbleToMerge,
  getFeatureState,
  selectCurrentChangeRequest,
  selectChangeRequests,
  selectChangeRequestsMap,
  selectAllChangeItems,
  selectChangeItemEntities,
  selectPreviewScope,
  selectPreviewConfiguration,
  selectPreviewDeliverables,
  selectPreviewDeliverablesMap,

  selectMappedDeliverables,
  selectMappedUnsectionedDeliverables,
  selectMappedTrackedDeliverables,
  selectMappedUnsectionedTrackedDeliverables,
  selectMappedDeliverableSections,

  selectNetChangesUnsectionedDeliverables,
  selectNetChangesSections,
}

const groupBySection = (deliverables: Deliverable[]): Dictionary<Deliverable[]> => groupBy(deliverables, 'section.id')
const filterUnsectionedDeliverables = (deliverables: Deliverable[]): Deliverable[] =>
  deliverables.filter((deliverable) => !deliverable.section)
